import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useSnapshot } from 'valtio';
import { Eye, EyeOff } from 'lucide-react';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropUtils'; // Helper function defined below

import API from '../../lib/api';
import { state } from '../../store/global';
import findMessage from '../../utils/messages'; // Helper function defined below

import Inputbox from '../common/Inputbox';
import DateInputbox from '../common/DateInputbox';
import PhoneNumberInputbox from '../common/PhoneNumberInputbox';
import LocationInputbox from '../common/LocationInputbox';
import SuccessMessage from '../common/SuccessMessage';

import UserIcon from '../svg/UserIcon';
import LockIcon from '../svg/LockIcon';
import XIcon from '../svg/XIcon';
import PencilIcon from '../svg/PencilIcon';

const menu = [
  {
    title: 'Mi perfil',
    icon: (color) => <UserIcon color={color} />,
  },
  {
    title: 'Seguridad',
    icon: (color) => <LockIcon color={color} />,
  },
];

export default function ProfileModal({ selected, onChange }) {
  const snap = useSnapshot(state);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [address, setAddress] = useState('');
  const [country, setCountry] = useState('');
  const [birthday, setBirthday] = useState('');
  const [isEditPhone, setIsEditPhone] = useState(0);
  const [password, setPassword] = useState('');
  const [phonenumber, setPhonenumber] = useState({
    nationality: {
      country: '',
      areaCode: '',
      flag: '',
    },
    number: '',
  });
  const [errorMessage, setErrorMessage] = useState('');

  const onChangePhonenumber = (type, value) => {
    let newPhone = { ...phonenumber };
    newPhone[type] = value;
    setPhonenumber(newPhone);
  };

  useEffect(() => {
    setPhonenumber({
      nationality: {
        country: snap.user.phonenumber.nationality.country || '',
        areaCode: snap.user.phonenumber.nationality.area_code || '',
        flag: '',
      },
      number: snap.user.phonenumber.number || '',
    });
  }, [snap.user.phonenumber]);

  useEffect(() => {
    setFirstName(snap.user.firstName || '');
    setLastName(snap.user.lastName || '');
    setAddress(snap.user.address || '');
    setCountry(snap.user.country || '');
    setBirthday(snap.user?.birthday ? snap.user?.birthday.slice(0, 10) : '');
  }, [snap.user]);

  const isPossibleUpdateProfile = () => {
    return firstName && lastName && address && country && birthday;
  };
  const onUpdateProfile = async () => {
    if (!isPossibleUpdateProfile()) return;

    await API.post('user/updateProfile', {
      firstName: firstName,
      lastName: lastName,
      address: address,
      country: country,
      birthday: birthday,
    })
      .then((res) => {
        console.log('res: ', res.data);
        const user = {
          id: snap.user.id,
          role: snap.user.role,
          customerId: snap.user.customerId,
          firstName: firstName,
          lastName: lastName,
          email: snap.user.email,
          phonenumber: snap.user.phonenumber,
          phoneVerified: true,
          avatar: snap.user.avatar,
          address: address,
          birthday: birthday,
          country: country,
        };
        state.setUser(user);
        onChange('');
      })
      .catch((err) => {
        console.log('err: ', err);
        setErrorMessage(findMessage(err.response.data.message));
      });
  };

  const [verifyCode, setVerifyCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const [error, setError] = useState(false);
  const onInputVerifyCode = (index, value) => {
    if (!/^\d?$/.test(value)) return; // allow only one digit

    const updated = [...verifyCode];
    updated[index] = value;
    setVerifyCode(updated);

    // Move to next input if value is entered
    if (value && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Move to previous input if value is entered
    if (!value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const isFullInputed = () => {
    return verifyCode.every((code) => code.trim() !== '');
  };

  const [invalidPhonenumber, setInvalidPhonenumber] = useState(false);
  const [resendTimer, setResendTimer] = useState(0); // 60 seconds
  useEffect(() => {
    if (resendTimer <= 0) return;

    const interval = setInterval(() => {
      setResendTimer((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(interval); // cleanup
  }, [resendTimer]);
  const updatePhonenumber = async () => {
    if (!password || !phonenumber.nationality.areaCode || !phonenumber.number)
      return;

    const newPhonenumber = {
      nationality: {
        area_code: phonenumber.nationality.areaCode,
        country: phonenumber.nationality.country,
      },
      number: phonenumber.number,
    };

    await API.post('user/updatePhonenumberRequest', {
      password: password,
      phonenumber: newPhonenumber,
    })
      .then((res) => {
        console.log('res: ', res.data);
        setErrorMessage('');
        state.onChangeUserInfo('phonenumber', newPhonenumber);
        setIsEditPhone(2);
        setResendTimer(59);
      })
      .catch((err) => {
        console.log('err: ', err);
        const errorMsg = err.response?.data?.message || err.message;
        if (errorMsg.includes('Invalid parameter')) {
          setInvalidPhonenumber(true);
          setWrongPass(false);
        } else if (errorMsg == 'Password is not matched') {
          setInvalidPhonenumber(false);
          setWrongPass(true);
        } else {
          setInvalidPhonenumber(false);
          setWrongPass(false);
          setErrorMessage(findMessage(errorMsg));
        }
      });
  };
  const onConfirmPhoneVerify = async () => {
    if (!isFullInputed()) return;
    await API.post('user/onPhoneVerify', {
      phonenumber: phonenumber.nationality.areaCode + phonenumber.number,
      verifyCode: verifyCode.join(''),
    })
      .then((res) => {
        console.log('res: ', res.data);
        setErrorMessage('');
        setError(false);
        setIsEditPhone(3);
      })
      .catch((err) => {
        console.log('err: ', err);
        setError(true);
      });
  };
  const onResendVerifyCode = async () => {
    await API.post('user/resendVerifyCodeToPhone', {
      phonenumber: phonenumber.nationality.areaCode + phonenumber.number,
    })
      .then((res) => {
        console.log('res: ', res.data);
        setErrorMessage('');
        setResendTimer(59);
      })
      .catch((err) => {
        console.log('err: ', err);
        const errorMsg = err.response.data.message;
        setErrorMessage(findMessage(errorMsg));
      });
  };

  const [currentPass, setCurrentPass] = useState('');
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [wrongPass, setWrongPass] = useState(false);
  const [newPass, setNewPass] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);
  const [confirmPass, setConfirmPass] = useState('');
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [changePasswordProgress, setChangePasswordProgress] = useState(0);

  const isStrongPassword = () => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
    return regex.test(newPass) && newPass == confirmPass;
  };

  const onChangePassword = async () => {
    await API.post('user/updatePassword', {
      currentPassword: currentPass,
      newPassword: newPass,
    })
      .then((res) => {
        console.log('res: ', res.data);
        setErrorMessage('');
        setWrongPass(false);
        setChangePasswordProgress(1);
      })
      .catch((err) => {
        console.log('err: ', err);
        const errorMsg = err.response.data.message;
        if (errorMsg == 'Password is not matched') setWrongPass(true);
        else setErrorMessage(findMessage(errorMsg));
      });
  };

  const [uploadImageProgress, setUploadImageProgress] = useState(0);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(10);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const showCroppedImage = useCallback(async () => {
    try {
      const croppedImg = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCroppedImage(croppedImg);
    } catch (e) {
      console.error(e);
    }
  }, [imageSrc, croppedAreaPixels]);

  const onFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageDataUrl = await readFile(file);
      setImageSrc(imageDataUrl);
      setUploadImageProgress(1);
    }
  };
  const onUploadImage = async () => {
    await API.post('user/updateAvatar', {
      avatar: croppedImage,
    })
      .then((res) => {
        console.log('res: ', res.data);
        state.onChangeUserInfo('avatar', res.data.avatar);
        setErrorMessage('');
        setUploadImageProgress(2);
      })
      .catch((err) => {
        console.log('err: ', err);
        setErrorMessage(findMessage(err.response.data.message));
      });
  };

  return (
    <div className="relative">
      <div className="fixed z-50 inset-0 bg-black opacity-30 w-full h-full"></div>
      <div className="z-51 fixed inset-0 p-8"></div>

      <div className="z-51 fixed inset-0 flex items-center justify-center">
        <div className="w-full mx-4 md:mx-0 md:w-[825px] bg-white shadow-xl rounded-[20px]">
          <div className="p-6 pb-4 flex border-b-1 border-b-[#CDD9F0] items-center justify-between">
            <p className="inline min-w-21 font-[poppins] text-base text-[#181D27] font-medium leading-[20px]">
              Mi Cuenta
            </p>
            <p className="w-full text-center font-[poppins] text-base text-[#F04438] font-medium leading-[20px]">
              {errorMessage}
            </p>
            <button
              onClick={() => onChange('')}
              className="cursor-pointer hover:bg-red-500"
            >
              <XIcon size="w-[16px] h-[16px]" color="#414651" />
            </button>
          </div>

          <div className="flex h-[487px]">
            {/* left panel */}
            <div className="w-[202px] min-w-[202px] bg-[#FDFDFD] border-r-1 border-r-[#CDD9F0] rounded-bl-[20px]">
              <div className="py-4 space-y-2">
                <div className="flex items-center justify-center">
                  <div className="relative w-[103px] h-[103px] p-[6.4px] bg-gradient-to-r from-[#A3C0FF] to-[#ECF2FF] rounded-full flex items-center justify-center">
                    {snap.user.avatar || croppedImage ? (
                      <img
                        src={croppedImage ? croppedImage : snap.user.avatar}
                        alt="Avatar Image"
                        className="w-[94px] h-[94px] rounded-full"
                      />
                    ) : (
                      <div className="w-[94px] h-[94px] rounded-full bg-gradient-to-r from-[#092F7B] to-[#1056E1] flex items-center justify-center">
                        <p className="font-[poppins] text-[38px] text-white font-light">
                          {snap.user.firstName[0].toUpperCase() +
                            snap.user.lastName[0].toUpperCase()}
                        </p>
                      </div>
                    )}

                    <button
                      onClick={() => onChange('avatar')}
                      className="absolute bottom-1 right-1 w-7 h-7 bg-[#092F7B] rounded-full cursor-pointer flex items-center justify-center"
                    >
                      <PencilIcon />
                    </button>
                  </div>
                </div>
                <div className="text-center">
                  <p className="font-[poppins] px-2 text-base text-[#181D27] font-medium leading-[20px] break-all whitespace-normal">
                    {snap.user.firstName + ' ' + snap.user.lastName}
                  </p>
                  <p className="font-[poppins] px-2 text-sm text-[#717680] font-normal leading-[20px] break-all whitespace-normal">
                    {snap.user.email}
                  </p>
                </div>
              </div>

              {menu.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setIsEditPhone(0);
                    setWrongPass(false);
                    setInvalidPhonenumber(false);
                    setErrorMessage('');
                    onChange(item.title);
                  }}
                  className={`w-full p-4 hover:bg-gray-200 space-x-2 cursor-pointer flex items-center ${
                    selected == item.title ? 'bg-[#E6EBF7]' : 'bg-white'
                  }`}
                >
                  {item.icon(selected == item.title ? '#092F7B' : '#414651')}
                  <p
                    className={`font-[poppins] text-sm font-medium leading-[22px] ${
                      selected == item.title
                        ? 'text-[#092F7B]'
                        : 'text-[#414651]'
                    }`}
                  >
                    {item.title}
                  </p>
                </button>
              ))}
            </div>

            {/* right panel */}
            {selected == 'avatar' ? (
              <>
                {uploadImageProgress < 2 && (
                  <div className="w-full p-4 pr-10">
                    <div className="space-y-4">
                      <div className="space-y-2.5">
                        <p className="font-[poppins] text-base text-[#181D27] font-semibold leading-[20px]">
                          Editar avatar
                        </p>
                        <p className="font-[poppins] text-sm text-[#717680] font-normal leading-[22px]">
                          {uploadImageProgress == 0
                            ? 'Sube o modifica tu foto de perfil. Formatos permitidos: JPG. o PNG.'
                            : 'Ajusta la foto al óvalo; esa será la parte que se usará en tu avatar.'}
                        </p>

                        <div className="relative w-full h-50  bg-[#E6EBF7] rounded-[20px] flex items-center justify-center">
                          {uploadImageProgress == 0 ? (
                            <div className="w-33 h-33 bg-gradient-to-r from-[#092F7B] to-[#1056E1] rounded-full flex items-center justify-center">
                              <p className="font-[poppins] text-[50px] text-white font-light">
                                AR
                              </p>
                            </div>
                          ) : (
                            <div>
                              <style>{`
                                .reactEasyCrop_CropArea {
                                  border-radius: 9999px !important;
                                  overflow: hidden;
                                }
                              `}</style>
                              {imageSrc && (
                                <Cropper
                                  image={imageSrc}
                                  crop={crop}
                                  zoom={zoom}
                                  aspect={1}
                                  minZoom={1}
                                  maxZoom={20}
                                  showGrid={false}
                                  zoomWithScroll={true}
                                  onCropChange={setCrop}
                                  onZoomChange={setZoom}
                                  onCropComplete={onCropComplete}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {uploadImageProgress == 0 && (
                        <label className="float-right px-6 py-2 font-[poppins] text-sm text-[#FDFDFD] font-medium bg-[#092F7B] cursor-pointer leading-[22px] rounded-[10px]">
                          Subir imagen
                          <input
                            type="file"
                            accept="image/*"
                            onChange={onFileChange}
                            className="hidden"
                          />
                        </label>
                      )}

                      {uploadImageProgress == 1 && (
                        <div className="float-right inline-flex space-x-4">
                          {imageSrc && (
                            <button
                              onClick={showCroppedImage}
                              className="px-6 py-2 font-[poppins] text-sm text-[#FDFDFD] font-medium bg-[#092F7B] cursor-pointer leading-[22px] rounded-[10px]"
                            >
                              Recortar imagen
                            </button>
                          )}
                          {imageSrc && (
                            <button
                              onClick={onUploadImage}
                              className="px-6 py-2 font-[poppins] text-sm text-[#FDFDFD] font-medium bg-[#092F7B] cursor-pointer leading-[22px] rounded-[10px]"
                            >
                              Confirmar imagen
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {uploadImageProgress == 2 && (
                  <div className="h-full p-4 pr-6">
                    <div className="py-21">
                      <div className="px-31">
                        <SuccessMessage
                          title="Avatar actualizado"
                          description={[
                            'Tu imagén de perfil fue actualizada exitosamente',
                          ]}
                          buttonText="Volver al inicio"
                          onClick={() => setUploadImageProgress(0)}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : selected == 'Mi perfil' ? (
              <>
                {isEditPhone == 0 && (
                  <div className="w-full p-4 pr-10">
                    <p className="font-[poppins] text-base text-[#181D27] pb-2.5 font-semibold leading-[20px]">
                      Datos personales
                    </p>
                    <p className="font-[poppins] text-sm text-[#717680] font-normal pb-4 leading-[22px]">
                      Para modificar tus datos ineditables, comunicate con
                      nuestro soporte.{' '}
                    </p>

                    <div className="space-y-3 pb-10">
                      <div className="flex space-x-7">
                        <div className="flex-1">
                          <Inputbox
                            disabled={false}
                            title="Nombre"
                            placeholder="Ana"
                            value={firstName}
                            onChange={setFirstName}
                          />
                        </div>
                        <div className="flex-1">
                          <Inputbox
                            disabled={false}
                            title="Apellido"
                            placeholder="Rogriguez"
                            value={lastName}
                            onChange={setLastName}
                          />
                        </div>
                      </div>

                      <LocationInputbox
                        address={address}
                        setAddress={setAddress}
                      />

                      <div className="flex space-x-7">
                        <div className="flex-1">
                          <Inputbox
                            title="País"
                            placeholder="Colombia"
                            value={country}
                            onChange={setCountry}
                          />
                        </div>
                        <div className="flex-1">
                          <DateInputbox
                            title="Fecha de nacimiento"
                            value={birthday}
                            onChange={setBirthday}
                          />
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <div className="w-1/2 space-y-1">
                          <PhoneNumberInputbox
                            disabled={true}
                            phonenumber={phonenumber}
                            onChange={(type, value) =>
                              onChangePhonenumber(type, value)
                            }
                          />
                        </div>
                        <div className="w-1/2 space-y-1">
                          <p className="h-5 text-xs text-[#424242] font-normal"></p>
                          <button
                            onClick={() => setIsEditPhone(1)}
                            className="h-[38px] flex items-center font-[poppins] text-xs text-[#092F7B] underline cursor-pointer"
                          >
                            Editar teléfono
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="float-right">
                      <button
                        disabled={isPossibleUpdateProfile() ? false : true}
                        onClick={onUpdateProfile}
                        className={`float-right px-6 py-2 font-[poppins] text-sm ${
                          isPossibleUpdateProfile()
                            ? 'text-[#FDFDFD] bg-[#092F7B]'
                            : 'text-[#A4A7AE] bg-[#F5F5F5]'
                        } font-medium cursor-pointer leading-[22px] rounded-[10px]`}
                      >
                        Actualizar
                      </button>
                    </div>
                  </div>
                )}

                {isEditPhone == 1 && (
                  <div className="w-full p-4 pr-10 space-y-4">
                    <div className="space-y-2.5">
                      <p className="font-[poppins] text-base text-[#181D27] pb-2.5 font-semibold leading-[20px]">
                        Editar teléfono
                      </p>

                      {/* Phone number set */}
                      <div className="space-y-1">
                        <Inputbox
                          disabled={false}
                          title="Contraseña actual"
                          placeholder="Contraseña123#"
                          value={password}
                          onChange={setPassword}
                        />
                        <p className="font-[poppins] text-xs text-[#F04438] font-normal">
                          {wrongPass ? 'Contraseña incorrecta' : ''}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <PhoneNumberInputbox
                          phonenumber={phonenumber}
                          onChange={onChangePhonenumber}
                        />

                        <p className="font-[poppins] text-xs text-[#F04438] font-normal">
                          {invalidPhonenumber
                            ? 'Número de teléfono inválido'
                            : ''}
                        </p>
                      </div>
                    </div>

                    <div className="float-right">
                      <button
                        disabled={
                          password &&
                          phonenumber.nationality.areaCode &&
                          phonenumber.number
                            ? false
                            : true
                        }
                        onClick={updatePhonenumber}
                        className={`px-6 py-2 font-[poppins] ${
                          password &&
                          phonenumber.nationality.areaCode &&
                          phonenumber.number
                            ? 'text-[#FDFDFD] bg-[#092F7B]'
                            : 'text-[#A4A7AE] bg-[#F5F5F5]'
                        } text-sm rounded-[10px] cursor-pointer leading-[22px] tracking-[0%]`}
                      >
                        Editar teléfono
                      </button>
                    </div>
                  </div>
                )}

                {isEditPhone == 2 && (
                  <div className="w-full p-4 pr-6">
                    <p className="font-[poppins] text-base text-[#181D27] pb-2.5 font-semibold leading-[20px]">
                      Autenticación de dos pasos
                    </p>
                    <p className="font-[poppins] text-sm text-[#717680] font-normal leading-[22px]">
                      Ingresa el código que enviamos a tu nuevo teléfono
                      <strong>
                        {' ' + phonenumber.nationality.areaCode}*********
                        {phonenumber.number.slice(-4)}
                      </strong>
                    </p>

                    <div className="w-full py-10">
                      <div className="flex items-center justify-center pb-2">
                        <div className="space-x-4 inline-flex">
                          {verifyCode.map((val, idx) => (
                            <input
                              key={idx}
                              ref={(el) => (inputRefs.current[idx] = el)}
                              type="text"
                              value={val}
                              onChange={(e) =>
                                onInputVerifyCode(idx, e.target.value)
                              }
                              placeholder="_"
                              className={`w-12 h-12 font-[poppins] focus:outline-none focus:border-[#414651] placeholder-[#2A59C6] p-4 rounded-[10px] text-[20px] leading-[20px] text-center ${
                                error
                                  ? 'border border-[#f04438] text-[#f04438] bg-[#FEF3F2]'
                                  : isFullInputed()
                                  ? 'border border-[#039855] text-[#039855] bg-[#F9F9F9]'
                                  : 'bg-[#E6EBF7]'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="min-h-5 font-[poppins] text-[#f04438] font-normal text-sm text-center leading-[20px] tracking-[0%]">
                        {error
                          ? 'El código ingresado no es correcto. Verifícalo e intenta nuevamente.'
                          : ' '}
                      </div>
                    </div>

                    <div className="float-right">
                      <div className="inline-flex space-x-1.5">
                        <button
                          disabled={resendTimer ? true : false}
                          className={`min-w-47 px-7.5 py-2 ${
                            resendTimer > 0
                              ? 'bg-[#F5F5F5] text-[#A4A7AE]'
                              : 'bg-[#092F7B] text-[#FDFDFD]'
                          } font-[poppins] text-sm font-normal border-1 border-[#E9EAEB] rounded-[10px] cursor-pointer leading-[20px] tracking-[0%]`}
                          onClick={onResendVerifyCode}
                        >
                          Reenviar en 00:
                          {resendTimer < 10 ? '0' + resendTimer : resendTimer}
                        </button>
                        <button
                          disabled={isFullInputed() ? false : true}
                          className={`font-[poppins] px-6 py-2 ${
                            !isFullInputed()
                              ? 'bg-[#F5F5F5] text-[#A4A7AE]'
                              : 'bg-[#092F7B] text-[#FDFDFD]'
                          } text-sm font-medium rounded-[10px] cursor-pointer leading-[20px] tracking-[0%]`}
                          onClick={onConfirmPhoneVerify}
                        >
                          Activar verificación
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {isEditPhone == 3 && (
                  <div className="h-full p-4 pr-6">
                    <div className="py-21">
                      <div className="px-31">
                        <SuccessMessage
                          title="Teléfono actualizado"
                          description={[
                            'Tu número se actualizó con éxito.',
                            'En adelante, usaremos este nuevo número para enviarte los códigos de verificación.',
                          ]}
                          buttonText="Volver al inicio"
                          onClick={() => setIsEditPhone(0)}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {changePasswordProgress == 0 && (
                  <div className="w-full p-4 pr-10">
                    <div className="space-y-2.5">
                      <p className="text-base text-[#181D27] font-semibold leading-[20px]">
                        Cambiar contraseña
                      </p>

                      <div className="space-y-1">
                        <div className="text-xs text-[#424242] font-normal leading-[20px] tracking-[0%]">
                          Contraseña actual
                        </div>
                        <div className="relative">
                          <input
                            type={showCurrentPass ? 'text' : 'password'}
                            value={currentPass}
                            onChange={(e) => setCurrentPass(e.target.value)}
                            placeholder="Contraseña123#"
                            className="w-full bg-white text-sm text-[#414651] font-normal border border-[#C2C2C2] focus:outline-none focus:border-[#414651] py-2 px-3 rounded-[10px] pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPass((prev) => !prev)}
                            className="absolute right-2 top-3 text-gray-500 cursor-pointer"
                          >
                            {showCurrentPass ? (
                              <EyeOff size={16} />
                            ) : (
                              <Eye size={16} />
                            )}
                          </button>
                        </div>
                        {wrongPass && (
                          <p className="font-[poppins] text-xs font-normal text-[#F04438] leading-[20px] tracking-[0%]">
                            Contraseña incorrecta
                          </p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <div className="text-xs text-[#424242] font-normal leading-[20px] tracking-[0%]">
                          Nueva contraseña
                        </div>
                        <div className="relative">
                          <input
                            type={showNewPass ? 'text' : 'password'}
                            value={newPass}
                            onChange={(e) => setNewPass(e.target.value)}
                            placeholder="Contraseña123#"
                            className="w-full bg-white text-sm text-[#414651] font-normal border border-[#C2C2C2] focus:outline-none focus:border-[#414651] py-2 px-3 rounded-[10px] pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPass((prev) => !prev)}
                            className="absolute right-2 top-3 text-gray-500 cursor-pointer"
                          >
                            {showNewPass ? (
                              <EyeOff size={16} />
                            ) : (
                              <Eye size={16} />
                            )}
                          </button>
                        </div>
                        <p className="text-xs font-normal leading-[20px]">
                          <span
                            className={
                              newPass.length < 8
                                ? 'text-[#A4A7AE]'
                                : 'text-[#027A48]'
                            }
                          >{`Incluir ${
                            newPass.length < 8 ? '•' : '✓'
                          }8 caractéres`}</span>
                          <span
                            className={
                              /[A-Z]/.test(newPass)
                                ? 'text-[#027A48]'
                                : 'text-[#A4A7AE]'
                            }
                          >{` o más ${
                            /[A-Z]/.test(newPass) ? '✓' : '•'
                          } mayúscula`}</span>
                          <span
                            className={
                              /\d/.test(newPass)
                                ? 'text-[#027A48]'
                                : 'text-[#A4A7AE]'
                            }
                          >{` ${
                            /\d/.test(newPass) ? '✓' : '•'
                          } 1 número`}</span>
                          <span
                            className={
                              /[^a-zA-Z0-9]/.test(newPass)
                                ? 'text-[#027A48]'
                                : 'text-[#A4A7AE]'
                            }
                          >{` ${
                            /[^a-zA-Z0-9]/.test(newPass) ? '✓' : '•'
                          } 1 símbolo especial ( !, # ó $).`}</span>
                        </p>
                      </div>

                      <div className="space-y-1">
                        <div className="text-xs text-[#424242] font-normal leading-[20px] tracking-[0%]">
                          Repetir nueva contraseña
                        </div>
                        <div className="relative">
                          <input
                            type={showConfirmPass ? 'text' : 'password'}
                            value={confirmPass}
                            onChange={(e) => setConfirmPass(e.target.value)}
                            placeholder="Contraseña123#"
                            className="w-full bg-white text-sm text-[#414651] font-normal border border-[#C2C2C2] focus:outline-none focus:border-[#414651] py-2 px-3 rounded-[10px] pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPass((prev) => !prev)}
                            className="absolute right-2 top-3 text-gray-500 cursor-pointer"
                          >
                            {showConfirmPass ? (
                              <EyeOff size={16} />
                            ) : (
                              <Eye size={16} />
                            )}
                          </button>
                        </div>
                        <div className="text-xs font-normal text-gray-400 leading-[20px] tracking-[0%]">
                          <span
                            className={`${
                              confirmPass.length && newPass == confirmPass
                                ? 'text-[#027A48]'
                                : ''
                            }`}
                          >
                            {confirmPass.length && newPass == confirmPass
                              ? '✓'
                              : '•'}{' '}
                            Coincidencia de contraseñas
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8">
                      <button
                        disabled={isStrongPassword() ? false : true}
                        onClick={onChangePassword}
                        className={`float-right px-6 py-2 text-sm ${
                          isStrongPassword()
                            ? 'text-[#FDFDFD] bg-[#092F7B]'
                            : 'text-[#A4A7AE] bg-[#F5F5F5]'
                        } font-medium cursor-pointer leading-[22px] rounded-[10px]`}
                      >
                        Actualizar contraseña
                      </button>
                    </div>
                  </div>
                )}

                {changePasswordProgress == 1 && (
                  <div className="h-full p-4 pr-6">
                    <div className="py-21">
                      <div className="px-31">
                        <SuccessMessage
                          title="Contraseña actualizado"
                          description={[
                            'Tu contraseña fue actualizada exitosamente.',
                          ]}
                          buttonText="Volver al inicio"
                          onClick={() => setChangePasswordProgress(0)}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function readFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result));
    reader.readAsDataURL(file);
  });
}
